import * as React from 'react'
import { useMemo } from 'react'
import { Platform, View } from 'react-native'
import { Surface, Text } from 'react-native-paper'
import { Article } from '../types'
import {
  Contribution,
  ContributionType,
} from '../../../../../generated/graphql'
import { IconButton } from 'react-native-paper'
import {
  Icon3dChallenge,
  Icon3dEdit,
  Icon3dInquiry,
  IconClose,
  IconFullScreen,
} from '../../../../../utils/meldd-icons'
import { useTheme } from '../../../../../hooks/theme.hook'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../../../../@ui/typo/icon'
import { createStyle } from '../../../../../contexts/global-styles.provider'
import { Row } from '../../../../../@ui/containers/grid/row'
import { WhiteCard } from '../../../../../@ui/containers/cards/white.card'
import Column from '../../../../../@ui/containers/grid/column'
import { ContributionDecisionBox } from '../../../contributions/components/contribution-decision.box'
import { BumpButtons } from '../../../contributions/bump/bump.buttons'
import { useApolloClient } from '@apollo/client'
import { CancelContribution } from '../../../contributions/components/contribution.card'
import { useIsMobileDevice } from '../../../../../hooks/is-mobile.hook'
import { ContributionStatusChip } from '../../../contributions/components/contribution-status.chip'
import { t } from 'i18next'
import { NavigationButtons } from './contribution-navigation.button'
import { useTypeStrings } from '../../../../../hooks/type-strings.hook'

type NonReservedContributionType = Exclude<
  ContributionType,
  ContributionType.Reserved
>

interface ContributionDetailsProps {
  perspective: Article
  contributionId: string | null
  onNext: () => void
  onPrevious: () => void
  onClose: () => void
  readonly?: boolean
  onFullScreen: () => void
}

const useContributionTypeColors = (type: NonReservedContributionType) => {
  const theme = useTheme()
  const colors = {
    [ContributionType.Edit]: theme.colors.editBkg,
    [ContributionType.Challenge]: theme.colors.challengeBkg,
    [ContributionType.Inquiry]: theme.colors.inquiryBkg,
  }
  return colors[type as keyof typeof colors]
}

const HeaderIcon = ({ type }: { type: NonReservedContributionType }) => {
  const { t } = useTranslation('contributions')
  const isMobile = useIsMobileDevice()
  const headers = {
    [ContributionType.Inquiry]: {
      icon: Icon3dInquiry,
      type: ContributionType.Inquiry,
      label: t('Inquiry'),
    },
    [ContributionType.Challenge]: {
      icon: Icon3dChallenge,
      type: ContributionType.Challenge,
      label: t('Challenge'),
    },
    [ContributionType.OwnerEdit]: {
      icon: Icon3dEdit,
      type: ContributionType.OwnerEdit,
      label: t('Owner Edit'),
    },
    [ContributionType.Edit]: {
      icon: Icon3dEdit,
      type: ContributionType.Edit,
      label: t('Edit'),
    },
  }

  if (!headers[type]) return null
  return (
    <Row wrap={false} columnGap={2} alignItems="center">
      <Icon source={headers[type].icon} size={isMobile ? 20 : 24} />
      <Text variant={isMobile ? 'labelMedium' : 'titleMedium'}>
        {t(headers[type].label)}
      </Text>
    </Row>
  )
}

const PendingContribution = (
  props: ContributionDetailsProps & { contribution: Contribution }
) => {
  const { onClose, onNext, onPrevious, contribution } = props
  const styles = useStyle()
  const bkgColor = useContributionTypeColors(
    contribution?.intendedType as NonReservedContributionType
  )
  const { t } = useTranslation('contributions')
  return (
    <Surface
      style={[styles.container, { backgroundColor: bkgColor }]}
      elevation={3}
    >
      <Column gap={2} style={{ padding: 10 }}>
        <Row justifyContent="space-between">
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <HeaderIcon
              type={contribution.intendedType as NonReservedContributionType}
            />
          </View>
          <Row>
            <NavigationButtons
              onPrevious={onPrevious}
              onNext={onNext}
              disabled={false}
            />
            <IconButton onPress={onClose} icon={IconClose} />
          </Row>
        </Row>
        <Row>
          <View>
            <Text variant={'labelMedium'}>{t('editing')}</Text>
          </View>
        </Row>
      </Column>
    </Surface>
  )
}
export function ContributionDetails(props: ContributionDetailsProps) {
  const { contributionId, onClose, onNext, onPrevious, perspective, readonly } =
    props
  const { labelInput, labelMotivation } = useTypeStrings()
  const client = useApolloClient() // Add this hook
  const isMobile = useIsMobileDevice()

  const contribution: Contribution | undefined = useMemo(() => {
    return perspective.contributions.find(
      (contribution) => contribution.id === contributionId
    ) as Contribution
  }, [perspective, contributionId])

  const styles = useStyle()
  const bkgColor = useContributionTypeColors(
    contribution?.contributionType as NonReservedContributionType
  )

  if (!contribution) return null

  if (contribution.contributionType === ContributionType.Reserved) {
    return <PendingContribution {...props} contribution={contribution} />
  }

  return (
    <Surface
      style={[styles.container, { backgroundColor: bkgColor }]}
      elevation={3}
    >
      {
        <Column gap={2} style={{ padding: 10 }}>
          <Row justifyContent="space-between">
            <Row alignItems="center" columnGap={2}>
              <HeaderIcon
                type={
                  contribution.contributionType as NonReservedContributionType
                }
              />
              {!isMobile && (
                <ContributionStatusChip contribution={contribution} />
              )}
              <IconButton
                onPress={() => props.onFullScreen()}
                icon={IconFullScreen}
              />
            </Row>

            <Row>
              <NavigationButtons
                onPrevious={onPrevious}
                onNext={onNext}
                disabled={false}
              />
              <IconButton onPress={onClose} icon={IconClose} />
            </Row>
          </Row>
          {isMobile && (
            <Row justifyContent="flex-end">
              <ContributionStatusChip contribution={contribution} />
            </Row>
          )}
          {
            <WhiteCard>
              <View style={{ padding: 10, gap: 2 }}>
                <Text variant={'labelMedium'}>
                  {labelInput[contribution.contributionType]}:{' '}
                </Text>
                <Text variant={'bodyMedium'}>"{contribution.input}"</Text>
              </View>
            </WhiteCard>
          }
          {contribution.motivation && (
            <WhiteCard>
              <View style={{ padding: 10, gap: 2 }}>
                <Text variant={'labelMedium'}>
                  {labelMotivation[contribution.contributionType]}:{' '}
                </Text>
                <Text variant={'bodyMedium'}>"{contribution.motivation}"</Text>
              </View>
            </WhiteCard>
          )}

          {contribution.response && (
            <WhiteCard>
              <View style={{ padding: 10, gap: 2 }}>
                <Text variant={'labelMedium'}>{t('response')}: </Text>
                <Text variant={'bodyMedium'}>"{contribution.response}"</Text>
              </View>
            </WhiteCard>
          )}

          {!readonly && perspective.isOwner && (
            <ContributionDecisionBox
              contribution={contribution}
              onStatusUpdated={() => {
                client.refetchQueries({ include: ['article'] })
              }}
            />
          )}
          <Row justifyContent="space-between">
            <View>
              {!readonly && (
                <CancelContribution
                  contribution={contribution as Contribution}
                />
              )}
            </View>
            <BumpButtons contribution={contribution} readonly={readonly} />
          </Row>
        </Column>
      }
    </Surface>
  )
}

const useStyle = createStyle(({ theme }) => {
  return {
    container: {
      position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      paddingVertical: theme.spacing(5),
      marginTop: -theme.spacing(5),
      paddingHorizontal: theme.spacing(4),
      borderTopLeftRadius: theme.spacing(5),
      borderTopRightRadius: theme.spacing(5),
      flexDirection: 'column',
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: 'auto',
    },
    buttons: {
      paddingVertical: 5,
      flexDirection: 'row',
      flexWrap: 'nowrap',
      justifyContent: 'space-between',
    },
  }
})
